import React, { useState } from "react";
import { Eye, Download, RefreshCcw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError, API } from "@/lib/api";
import { TID } from "@/lib/testIds";

export const ResumesPanel = ({ rows }) => {
    const [viewing, setViewing] = useState(null);

    const downloadResume = async (row) => {
        try {
            const token = localStorage.getItem("simonara_admin_token");
            const res = await fetch(`${API}/admin/resumes/${row.id}/download`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) {
                let msg = `Download failed (HTTP ${res.status})`;
                try {
                    const j = await res.json();
                    if (j?.detail) msg = typeof j.detail === "string" ? j.detail : msg;
                } catch (_) { /* ignore */ }
                throw new Error(msg);
            }
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = row.resume_filename || `${row.name || "resume"}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(err?.message || "Failed to download.");
        }
    };

    const requestReupload = async (row) => {
        try {
            const { data } = await api.post(`/admin/resumes/${row.id}/request-reupload`);
            const origin = window.location.origin;
            const fullUrl = `${origin}${data.reupload_path}`;
            try {
                await navigator.clipboard.writeText(fullUrl);
                toast.success(`Re-upload link copied to clipboard. Share with ${data.email || data.name}.`);
            } catch (_) {
                toast.success(`Re-upload link: ${fullUrl}`);
            }
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    if (!rows || rows.length === 0) {
        return <div className="card-soft p-8 text-center text-brand-muted">No candidate registrations yet.</div>;
    }
    return (
        <>
            <div className="space-y-4">
                {rows.map((row) => {
                    const snippet = (row.resume_text || "").replace(/\s+/g, " ").trim().slice(0, 220);
                    return (
                        <div key={row.id} className="card-soft p-6">
                            <div className="flex items-start justify-between gap-4 flex-wrap">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="font-heading font-semibold text-brand-navy dark:text-slate-100 text-lg">{row.name}</h3>
                                        {row.years_experience && <Badge variant="outline" className="text-xs">{row.years_experience} yrs</Badge>}
                                        {(row.job_type_preferences || []).map((p) => (
                                            <Badge key={p} variant="secondary" className="bg-brand-softGreen text-brand-green border-0 text-xs">{p}</Badge>
                                        ))}
                                    </div>
                                    <div className="text-sm text-brand-muted mt-1 flex flex-wrap gap-x-3 gap-y-1">
                                        <span>{row.email}</span>
                                        {row.phone && <span>· {row.phone}</span>}
                                        {row.city && <span>· {row.city}</span>}
                                        {row.desired_position && <span>· Wants: {row.desired_position}</span>}
                                    </div>
                                    <div className="text-xs text-brand-muted mt-1 flex flex-wrap gap-x-3">
                                        {row.languages_spoken && <span>Languages: {row.languages_spoken}</span>}
                                        {row.desired_salary && <span>· Salary: {row.desired_salary}</span>}
                                        {row.availability && <span>· Available: {row.availability}</span>}
                                        {row.work_authorization && <span>· Auth: {row.work_authorization}</span>}
                                        {row.linkedin_url && (
                                            <a href={row.linkedin_url} target="_blank" rel="noreferrer" className="text-brand-green hover:underline">
                                                · LinkedIn
                                            </a>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-col items-end gap-2 text-xs text-brand-muted shrink-0">
                                    <span>{row.created_at ? new Date(row.created_at).toLocaleString() : ""}</span>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setViewing(row)}
                                            data-testid={TID.admin.resumeView(row.id)}
                                            className="px-3 py-1.5 rounded-lg border border-border hover:border-brand-navy text-xs flex items-center gap-1.5"
                                        >
                                            <Eye size={13} /> View text
                                        </button>
                                        {row.grid_file_id ? (
                                            <button
                                                onClick={() => downloadResume(row)}
                                                data-testid={TID.admin.resumeDownload(row.id)}
                                                className="px-3 py-1.5 rounded-lg bg-brand-navy text-white text-xs flex items-center gap-1.5 hover:bg-brand-navyHover"
                                            >
                                                <Download size={13} /> PDF
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => requestReupload(row)}
                                                data-testid={`admin-resume-reupload-${row.id}`}
                                                title="Generate a re-upload link to send this candidate"
                                                className="px-3 py-1.5 rounded-lg bg-brand-green text-white text-xs flex items-center gap-1.5 hover:bg-brand-greenHover"
                                            >
                                                <RefreshCcw size={13} /> Request re-upload
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {snippet && (
                                <div className="mt-4 p-4 rounded-xl bg-brand-sand dark:bg-muted border border-border">
                                    <div className="text-xs uppercase tracking-wider text-brand-muted mb-1.5">Resume snippet · {row.parsed_chars?.toLocaleString() || 0} chars parsed</div>
                                    <p className="text-sm text-brand-text dark:text-slate-200 leading-relaxed line-clamp-3">{snippet}{(row.resume_text || "").length > 220 ? "…" : ""}</p>
                                </div>
                            )}
                            {row.notes && (
                                <div className="mt-3 text-sm text-brand-muted italic">Note: {row.notes}</div>
                            )}
                        </div>
                    );
                })}
            </div>

            <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-brand-navy dark:text-slate-100">{viewing?.name} — Parsed Resume Text</DialogTitle>
                        <DialogDescription>
                            {viewing?.resume_filename} · {viewing?.parsed_chars?.toLocaleString() || 0} characters extracted
                        </DialogDescription>
                    </DialogHeader>
                    <div className="overflow-y-auto bg-brand-sand dark:bg-muted rounded-xl p-4 border border-border">
                        <pre className="text-xs text-brand-text dark:text-slate-200 whitespace-pre-wrap font-mono leading-relaxed">
                            {viewing?.resume_text || "(No text could be extracted from this PDF — it may be an image scan.)"}
                        </pre>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ResumesPanel;
